import { Injectable, inject, signal } from '@angular/core';
import { Observable, catchError, of, forkJoin, firstValueFrom } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { AnalyticsService, QueryType } from '../../../../../../core/services/analytics.service';
import { SocAnalyticsService } from 'src/app/core/services/analytics/soc-analytics.service';

export interface SocAnalyticsParams {
  /** Date range for alert-based queries (e.g., source map, destination map, suspect users) */
  alert_received_on__range?: string;
  /** Date range for ticket-based queries (e.g., outcome status, MTTR data) */
  created_time__range?: string;
  /** Filter for escalated cases (used in MTTD calculations) */
  is_escalated?: boolean | null;
  /** Username filter for user-specific queries */
  username__icontains?: string;
  /** Source IP filter for asset-specific queries */
  source_ip?: string;
  /** Start date for partner analytics queries */
  startDate?: string;
  /** End date for partner analytics queries */
  endDate?: string;
  // filter type. In soc v2 analytics it will be "filtered"
  type?: string;
  /** Security tool filter - comma-separated list of product IDs */
  security_tool?: string;
}

/**
 * Note: This service uses separate parameter objects for different data types:
 * - alertParams: for alert-based data (source map, destination map, suspect users, etc.)
 * - ticketDataParams: for ticket-based data (outcome status, MTTR data)
 * - mttdParams: for MTTD calculations (includes both alert date range and escalation status)
 *
 * This matches the original working implementation pattern.
 */
export interface SocAnalyticsResponse {
  success: boolean;
  data?: {
    sourceMap?: any;
    destinationMap?: any;
    topSuspectUsers?: any;
    topDetections?: any;
    topSuspectAssets?: any;
    outcomeStatus?: any;
    disposition?: any;
    adrManual?: any;
    mttd?: any;
    mttrAverage?: any;
    mttrData?: any;
    ticketClosureBreakdown?: any;
    slaAdherence?: any;
    mttdData?: any;
    topDetectionsData?: any;
    mttdLineGraphData?: any;
    mttrLineGraphData?: any;
    alertStatSummary?: any;
    ticketTrends?: any;
    mttdStatData?: any;
    mttrStatData?: any;
    eventsVsEscalationsChartData?: any;
    slaAdherenceData?: any;
    topMttdDetectionsData?: any;
    ticketsDispositionData?: any;
    ticketCategoryPerformanceData?: any;
    automatedResponseUsageData?: any;
    customerDispositionData?: any;
    ticketClosureOutcomesData?: any;
  };
  error?: string;
}

@Injectable({
  providedIn: 'root',
})
export class SocDataService {
  private analyticsService = inject(AnalyticsService);
  private socAnalyticsService = inject(SocAnalyticsService);
  public startDate = signal<Date | null>(new Date(new Date().setDate(new Date().getDate() - 365)));
  public endDate = signal<Date | null>(new Date());

  // @MF: Leaving this hear for backwards compatibility until we implement fully on admin dashboard component
  public getSocAnalytics(orgId: number, params: SocAnalyticsParams): Observable<SocAnalyticsResponse> {
    try {
      // Create separate parameter objects for different data types (matching original implementation)
      const alertParams = {
        alert_received_on__range: params.alert_received_on__range,
        security_tool: params.security_tool,
        startDate: params.startDate,
        endDate: params.endDate,
        type: params.type,
      };

      const ticketDataParams = {
        created_time__range: params.created_time__range,
        startDate: params.startDate,
        endDate: params.endDate,
        type: params.type,
        security_tool: params.security_tool,
      };

      const mttdParams = {
        alert_received_on__range: params.alert_received_on__range,
        is_escalated: params.is_escalated,
        startDate: params.startDate,
        endDate: params.endDate,
        security_tool: params.security_tool,
      };

      // Use forkJoin to fetch all data concurrently and return structured response
      return forkJoin({
        eventsVsEscalationsChartData: this.fetchEventsVsEscalationsChartData(orgId, alertParams),
        sourceMap: this.fetchSourceMap(orgId, alertParams),
        destinationMap: this.fetchDestinationMap(orgId, alertParams),
        topSuspectUsers: this.fetchTopSuspectUsers(orgId, alertParams),
        topDetections: this.fetchTopDetections(orgId, alertParams),
        topSuspectAssets: this.fetchTopSuspectAssets(orgId, alertParams),
        outcomeStatus: this.fetchOutcomeStatus(orgId, ticketDataParams),
        disposition: this.fetchDisposition(orgId, alertParams),
        adrManual: this.fetchAdrManualData(orgId, alertParams),
        mttd: this.fetchMttd(orgId, mttdParams),
        mttrAverage: of(null), // Not used, removing duplicate API call
        mttrData: this.fetchMttrData(orgId, ticketDataParams),
        ticketClosureBreakdown: this.fetchTicketClosureBreakdownData(orgId, alertParams),
        slaAdherence: this.fetchSlaAdherence(orgId, alertParams),
        mttdData: this.fetchMttdData(orgId, mttdParams),
        topDetectionsData: this.fetchTopDetectionsData(orgId, alertParams),
        mttdLineGraphData: this.fetchMttdLineGraphData(orgId, alertParams),
        mttrLineGraphData: this.fetchMttrLineGraphData(orgId, ticketDataParams),
        mttdStatData: this.fetchMttdStatData(orgId, mttdParams),
        mttrStatData: this.fetchMttrStatData(orgId, ticketDataParams),
        alertStatSummary: this.fetchAlertStatSummary(
          orgId,
          QueryType.Filtered,
          params.startDate,
          params.endDate,
          params.security_tool
        ),
        ticketTrends: this.fetchTicketTrends(
          orgId,
          QueryType.Filtered,
          params.startDate,
          params.endDate,
          params.security_tool
        ),
        slaAdherenceData: this.fetchSlaVoilationsData(orgId, {
          startDate: params.startDate,
          endDate: params.endDate,
          security_tool: params.security_tool,
        }),
        topMttdDetectionsData: this.fetchTopMttdDetectionsData(orgId, alertParams),
        ticketsDispositionData: this.fetchTicketsDispositionData(orgId, alertParams),
        customerDispositionData: this.fetchCustomerDispositionData(orgId, alertParams),
        ticketCategoryPerformanceData: this.fetchTicketCategoryPerformanceData(orgId, alertParams),
        automatedResponseUsageData: this.fetchAutomatedResponseUsageData(orgId, alertParams),
        ticketClosureOutcomesData: this.fetchTicketClosureOutcomesData(orgId, alertParams),
      }).pipe(
        map((data) => ({
          success: true,
          data,
        })),
        catchError((error) => {
          console.error('Error in getSocAnalytics:', error);
          return of({
            success: false,
            error: 'Failed to fetch SOC analytics data',
          });
        })
      );
    } catch (error) {
      console.error('Error in getSocAnalytics:', error);
      return of({ success: false, error: 'Failed to initialize SOC analytics' });
    }
  }

  public fetchEventsVsEscalationsChartData(orgId: number | number[], params: SocAnalyticsParams): Observable<any> {
    if (!params.startDate || !params.endDate) {
      console.error('Error: Invalid date range provided for fetchEventsVsEscalationsChartData');
      return of(null);
    }

    return this.analyticsService
      .getEventsVsEscalationsChartData(
        orgId,
        params.startDate,
        params.endDate,
        params.type || QueryType.Filtered,
        params.security_tool
      )
      .pipe(
        catchError((error: any) => {
          console.error('Error fetching Events vs Escalations Chart Data:', error);
          return of(null);
        })
      );
  }

  /**
   * Fetch source map data
   * @param orgId - Organization ID
   * @param params - Parameters including alert_received_on__range for date filtering
   */
  public fetchSourceMap(orgId: number | number[], params: SocAnalyticsParams): Observable<any> {
    if (!params.startDate || !params.endDate) {
      console.error('Error: Invalid date range provided for fetchSourceMap');
      return of(null);
    }

    return this.analyticsService
      .getSourceMap(orgId, params.startDate, params.endDate, params.type || QueryType.Filtered, params.security_tool)
      .pipe(
        catchError((error: any) => {
          console.error('Error fetching Source Map:', error);
          return of(null);
        })
      );
  }

  /**
   * Fetch destination map data
   * @param orgId - Organization ID
   * @param params - Parameters including alert_received_on__range for date filtering
   */
  public fetchDestinationMap(orgId: number | number[], params: SocAnalyticsParams): Observable<any> {
    if (!params.startDate || !params.endDate) {
      console.error('Error: Invalid date range provided for fetchDestinationMap');
      return of(null);
    }

    return this.analyticsService
      .getDestinationMap(
        orgId,
        params.startDate,
        params.endDate,
        params.type || QueryType.Filtered,
        params.security_tool
      )
      .pipe(
        catchError((error: any) => {
          console.error('Error fetching Destination Map:', error);
          return of(null);
        })
      );
  }

  /**
   * Fetch top suspect users data
   * @param orgId - Organization ID
   * @param params - Parameters including alert_received_on__range for date filtering
   */
  public fetchTopSuspectUsers(orgId: number | number[], params: SocAnalyticsParams): Observable<any> {
    if (!params.startDate || !params.endDate) {
      console.error('Error: Invalid date range provided for fetchTopSuspectUsers');
      return of(null);
    }

    return this.analyticsService
      .getTopSuspectUsers(
        orgId,
        params.startDate,
        params.endDate,
        params.type || QueryType.Filtered,
        params.security_tool
      )
      .pipe(
        catchError((error: any) => {
          console.error('Error fetching Top Suspect Users:', error);
          return of(null);
        })
      );
  }

  /**
   * Fetch top detections data
   * @param orgId - Organization ID
   * @param params - Parameters including alert_received_on__range for date filtering
   */
  public fetchTopDetections(orgId: number | number[], params: SocAnalyticsParams): Observable<any> {
    if (!params.startDate || !params.endDate) {
      console.error('Error: Invalid date range provided for fetchTopDetections');
      return of(null);
    }

    return this.analyticsService
      .getTopUserDetail(
        orgId,
        params.startDate,
        params.endDate,
        params.type || QueryType.Filtered,
        params.security_tool,
        null
      )
      .pipe(
        catchError((error: any) => {
          console.error('Error fetching Top User Detail:', error);
          return of(null);
        })
      );
  }

  /**
   * Fetch top suspect assets data
   * @param orgId - Organization ID
   * @param params - Parameters including alert_received_on__range for date filtering
   */
  public fetchTopSuspectAssets(orgId: number | number[], params: SocAnalyticsParams): Observable<any> {
    if (!params.startDate || !params.endDate) {
      console.error('Error: Invalid date range provided for fetchTopSuspectAssets');
      return of(null);
    }

    return this.analyticsService
      .getTopSuspectAssets(
        orgId,
        params.startDate,
        params.endDate,
        params.type || QueryType.Filtered,
        params.security_tool
      )
      .pipe(
        catchError((error: any) => {
          console.error('Error fetching Top Suspect Assets:', error);
          return of(null);
        })
      );
  }

  /**
   * Fetch outcome status data
   * @param orgId - Organization ID
   * @param params - Parameters including created_time__range for ticket date filtering
   */
  public fetchOutcomeStatus(orgId: number | number[], params: SocAnalyticsParams): Observable<any> {
    return this.analyticsService.getOutcomeStatus(orgId, params).pipe(
      catchError((error: any) => {
        console.error('Error fetching Outcome Status:', error);
        return of(null);
      })
    );
  }

  /**
   * Fetch disposition data
   * @param orgId - Organization ID
   * @param params - Parameters including alert_received_on__range for date filtering
   */
  public fetchDisposition(orgId: number | number[], params: SocAnalyticsParams): Observable<any> {
    return this.analyticsService.getDisposition(orgId, params).pipe(
      catchError((error: any) => {
        console.error('Error fetching Disposition:', error);
        return of(null);
      })
    );
  }

  /**
   * Fetch ADR manual data
   * @param orgId - Organization ID
   * @param params - Parameters including startDate, endDate, and security_tool for filtering
   */
  public fetchAdrManualData(orgId: number | number[], params: SocAnalyticsParams): Observable<any> {
    if (!params.startDate || !params.endDate) {
      console.error('Error: Invalid date range provided for fetchAdrManualData');
      return of(null);
    }

    return this.analyticsService
      .getAdrManualData(
        orgId,
        params.startDate,
        params.endDate,
        params.type || QueryType.Filtered,
        params.security_tool
      )
      .pipe(
        catchError((error: any) => {
          console.error('Error fetching ADR vs Manual Data:', error);
          return of(null);
        })
      );
  }

  /**
   * Fetch MTTD data
   * @param orgId - Organization ID
   * @param params - Parameters including alert_received_on__range and is_escalated for filtering
   */
  public fetchMttd(orgId: number, params: SocAnalyticsParams): Observable<any> {
    return this.analyticsService.getMttd(orgId, params).pipe(
      catchError((error: any) => {
        console.error('Error fetching Average MTTD:', error);
        return of(null);
      })
    );
  }

  /**
   * Fetch MTTR average data
   * @param orgId - Organization ID
   * @param params - Parameters including created_time__range for ticket date filtering
   */
  public fetchMttrAverage(orgId: number, params: SocAnalyticsParams): Observable<any> {
    if (!params.startDate || !params.endDate) {
      console.error('Error: Invalid date range provided for fetchMttrAverage');
      return of(null);
    }

    return this.analyticsService
      .getAverageMttr(orgId, params.startDate, params.endDate, params.type || QueryType.Filtered)
      .pipe(
        catchError((error: any) => {
          console.error('Error fetching Average MTTR:', error);
          return of(null);
        })
      );
  }

  /**
   * Fetch MTTR data
   * @param orgId - Organization ID
   * @param params - Parameters including created_time__range for ticket date filtering
   */
  public fetchMttrData(orgId: number, params: SocAnalyticsParams): Observable<any> {
    return this.analyticsService.getMttrData(orgId, params).pipe(
      catchError((error: any) => {
        console.error('Error fetching MTTR Data:', error);
        return of(null);
      })
    );
  }

  /**
   * Fetch ticket closure breakdown data
   * @param orgId - Organization ID
   * @param params - Parameters including alert_received_on__range for date filtering
   */
  public fetchTicketClosureBreakdownData(orgId: number | number[], params: SocAnalyticsParams): Observable<any> {
    return this.analyticsService.getTicketClosureBreakdownData(orgId, params).pipe(
      catchError((error: any) => {
        console.error('Error fetching Ticket Closure Breakdown Data:', error);
        return of(null);
      })
    );
  }

  /**
   * Fetch SLA adherence data
   * @param orgId - Organization ID
   * @param params - Parameters including alert_received_on__range for date filtering
   */
  public fetchSlaAdherence(orgId: number, params: SocAnalyticsParams): Observable<any> {
    return this.analyticsService.getSlaAdherence(orgId, params).pipe(
      catchError((error: any) => {
        console.error('Error fetching SLA Adherence Data:', error);
        return of(null);
      })
    );
  }

  /**
   * Fetch MTTD data with specific parameters
   * @param orgId - Organization ID
   * @param params - Parameters including alert_received_on__range and is_escalated for filtering
   */
  public fetchMttdData(orgId: number, params: SocAnalyticsParams): Observable<any> {
    return this.analyticsService.getMttdData(orgId, params).pipe(
      catchError((error: any) => {
        console.error('Error fetching MTTD Data:', error);
        return of(null);
      })
    );
  }

  /**
   * Fetch top detections data
   * @param orgId - Organization ID
   * @param params - Parameters including startDate, endDate, security_tool, and is_escalated for filtering
   */
  public fetchTopDetectionsData(orgId: number | number[], params: SocAnalyticsParams): Observable<any> {
    if (!params.startDate || !params.endDate) {
      console.error('Error: Invalid date range provided for fetchTopDetectionsData');
      return of(null);
    }

    return this.analyticsService
      .getTopDetectionData(orgId, params.startDate, params.endDate, params.security_tool, params.is_escalated)
      .pipe(
        catchError((error: any) => {
          console.error('Error fetching Top Detections Data:', error);
          return of(null);
        })
      );
  }

  /**
   * Fetch top user detail data
   * @param orgId - Organization ID
   * @param params - Parameters including alert_received_on__range and username__icontains for filtering
   */
  public fetchTopUserDetail(orgId: number, params: SocAnalyticsParams): Observable<any> {
    if (!params.startDate || !params.endDate) {
      console.error('Error: Invalid date range provided for fetchTopUserDetail');
      return of(null);
    }

    return this.analyticsService
      .getTopUserDetail(
        orgId,
        params.startDate,
        params.endDate,
        params.type || QueryType.Filtered,
        params.security_tool,
        params.username__icontains || null
      )
      .pipe(
        catchError((error: any) => {
          console.error('Error fetching Top User Detail:', error);
          return of(null);
        })
      );
  }

  /**
   * Fetch top asset detail data
   */
  public fetchTopAssetDetailData(orgId: number, params: SocAnalyticsParams): Observable<any> {
    if (!params.startDate || !params.endDate || !params.source_ip) {
      console.error('Error: Invalid date range or source ip provided for fetchTopAssetDetailData');
      return of(null);
    }

    return this.analyticsService
      .getTopAssetDetail(
        orgId,
        params.startDate,
        params.endDate,
        params.source_ip,
        params.type || QueryType.Filtered,
        params.security_tool
      )
      .pipe(
        catchError((error: any) => {
          console.error('Error fetching Top Asset Detail:', error);
          return of(null);
        })
      );
  }

  public fetchAlertStatSummary(
    orgId: number | number[],
    queryType: QueryType,
    startDate?: string,
    endDate?: string,
    securityTool?: string | null | undefined
  ): Observable<any> {
    // Use provided dates or fall back to service defaults
    const finalStartDate = startDate || this.startDate()?.toISOString() || '';
    const finalEndDate = endDate || this.endDate()?.toISOString() || '';

    return this.analyticsService.getAlertStatSummary(orgId, finalStartDate, finalEndDate, queryType, securityTool).pipe(
      catchError((error: any) => {
        console.error('Error fetching Partner Alert Stat Summary:', error);
        // Return default structure on error
        return of({
          total_ingest_current: 0,
          total_ingest_previous: 0,
          total_ingest_pct_change: 0,
          unique_case_count_current: 0,
          unique_case_count_previous: 0,
          unique_case_count_pct_change: 0,
          ticket_count_current: 0,
          ticket_count_previous: 0,
          ticket_count_pct_change: 0,
        });
      })
    );
  }

  public fetchTicketTrends(
    orgId: number | number[],
    queryType: QueryType,
    startDate?: string,
    endDate?: string,
    securityTool?: string | null | undefined
  ): Observable<any> {
    // Use provided dates or fall back to service defaults
    const finalStartDate = startDate || this.startDate()?.toISOString() || '';
    const finalEndDate = endDate || this.endDate()?.toISOString() || '';

    return this.analyticsService
      .getTicketTrendsAnalytics(orgId, finalStartDate, finalEndDate, queryType, securityTool)
      .pipe(
        catchError((error: any) => {
          console.error('Error fetching Partner Ticket Trends:', error);
          // Return default structure on error
          return of({
            total_tickets: 0,
            resolved_tickets: 0,
            open_tickets: 0,
            ticket_trends: [],
          });
        })
      );
  }

  public fetchMttdLineGraphData(orgId: number | number[], params: SocAnalyticsParams): Observable<any> {
    if (!params.startDate || !params.endDate) {
      console.error('Error: Invalid date range provided for fetchMttdLineGraphData');
      return of(null);
    }

    return this.analyticsService
      .getMttdLineGraphData(
        orgId,
        params.startDate,
        params.endDate,
        params.type || QueryType.Filtered,
        params.security_tool
      )
      .pipe(
        catchError((error: any) => {
          console.error('Error fetching MTTD Line Graph Data:', error);
          return of(null);
        })
      );
  }

  public fetchMttrLineGraphData(orgId: number | number[], params: SocAnalyticsParams): Observable<any> {
    if (!params.startDate || !params.endDate) {
      console.error('Error: Invalid date range provided for fetchMttrLineGraphData');
      return of(null);
    }

    return this.analyticsService
      .getMttrLineGraphData(
        orgId,
        params.startDate,
        params.endDate,
        params.type || QueryType.Filtered,
        params.security_tool
      )
      .pipe(
        catchError((error: any) => {
          console.error('Error fetching MTTR Line Graph Data:', error);
          return of(null);
        })
      );
  }

  public fetchMttdStatData(orgId: number | number[], params: SocAnalyticsParams): Observable<any> {
    if (!params.startDate || !params.endDate) {
      console.error('Error: Invalid date range provided for fetchMttdStatData');
      return of(null);
    }

    return this.analyticsService
      .getPartnerMttd(orgId, params.startDate, params.endDate, params.type || QueryType.Filtered, params.security_tool)
      .pipe(
        map((response) => ({
          current_average_mttd: response.average_mttd_minutes_current,
          previous_average_mttd: response.average_mttd_minutes_previous,
          percent_difference: response.average_mttd_minutes_pct_change,
        })),
        catchError((error: any) => {
          console.error('Error fetching MTTD Stat Data:', error);
          return of(null);
        })
      );
  }

  public fetchMttrStatData(orgId: number | number[], params: SocAnalyticsParams): Observable<any> {
    if (!params.startDate || !params.endDate) {
      console.error('Error: Invalid date range provided for fetchMttrStatData');
      return of(null);
    }

    // Use the real API endpoint (getAverageMttr) instead of mock data
    return this.analyticsService
      .getAverageMttr(orgId, params.startDate, params.endDate, params.type || QueryType.Filtered, params.security_tool)
      .pipe(
        map((response) => ({
          // API returns hours, pass as hours
          current_average_mttr_hours: response.average_mttr_hours_current,
          previous_average_mttr_hours: response.average_mttr_hours_previous,
          percent_difference: response.average_mttr_hours_pct_change,
        })),
        catchError((error: any) => {
          console.error('Error fetching MTTR Stat Data:', error);
          return of(null);
        })
      );
  }

  public fetchSlaVoilationsData(orgId: number | number[], params: SocAnalyticsParams): Observable<any> {
    // Use partner SLA adherence endpoint with organization array and filtered type
    const startDate = params.startDate || '';
    const endDate = params.endDate || '';
    // Normalize orgId to array format
    const orgIds = Array.isArray(orgId) ? orgId : [orgId];

    return this.analyticsService.getSlaAdherenceV2(orgIds, startDate, endDate, 'filtered', params.security_tool).pipe(
      catchError((error: any) => {
        console.error('Error fetching SLA Violations Data:', error);
        return of(null);
      })
    );
  }

  public fetchTopMttdDetectionsData(orgId: number | number[], params: SocAnalyticsParams): Observable<any> {
    if (!params.startDate || !params.endDate) {
      console.error('Error: Invalid date range provided for fetchTopMttdDetectionsData');
      return of(null);
    }

    return this.analyticsService
      .getTopMttdDetectionsData(
        orgId,
        params.startDate,
        params.endDate,
        params.type || QueryType.Filtered,
        params.security_tool
      )
      .pipe(
        catchError((error: any) => {
          console.error('Error fetching Top MTTD Detections Data:', error);
          return of(null);
        })
      );
  }

  public fetchTicketsDispositionData(orgId: number | number[], params: SocAnalyticsParams): Observable<any[]> {
    if (!params.startDate || !params.endDate) {
      console.error('Error: Invalid date range provided for fetchTicketsDispositionData');
      return of([]);
    }

    return this.analyticsService
      .getTicketsDispositionData(
        orgId,
        params.startDate,
        params.endDate,
        params.type || QueryType.Filtered,
        params.security_tool
      )
      .pipe(
        catchError((error: any) => {
          console.error('Error fetching Tickets Disposition Data:', error);
          return of([]);
        })
      );
  }

  public fetchCustomerDispositionData(orgId: number | number[], params: SocAnalyticsParams): Observable<any> {
    if (!params.startDate || !params.endDate) {
      console.error('Error: Invalid date range provided for fetchCustomerDispositionData');
      return of([]);
    }

    return this.analyticsService
      .getCustomerDispositionData(
        orgId,
        params.startDate,
        params.endDate,
        params.type || QueryType.Filtered,
        params.security_tool
      )
      .pipe(
        catchError((error: any) => {
          console.error('Error fetching Customer Disposition Data:', error);
          return of([]);
        })
      );
  }

  public fetchTicketCategoryPerformanceData(orgId: number | number[], params: SocAnalyticsParams): Observable<any> {
    if (!params.startDate || !params.endDate) {
      console.error('Error: Invalid date range provided for fetchTicketCategoryPerformanceData');
      return of([]);
    }

    return this.analyticsService
      .getTicketCategoryPerformanceData(
        orgId,
        params.startDate,
        params.endDate,
        params.type || QueryType.Filtered,
        params.security_tool
      )
      .pipe(
        catchError((error: any) => {
          console.error('Error fetching Ticket Category Performance Data:', error);
          return of([]);
        })
      );
  }

  public fetchAutomatedResponseUsageData(orgId: number | number[], params: SocAnalyticsParams): Observable<any> {
    if (!params.startDate || !params.endDate) {
      console.error('Error: Invalid date range provided for fetchAutomatedResponseUsageData');
      return of([]);
    }

    return this.analyticsService
      .getAutomatedResponseUsageData(
        orgId,
        params.startDate,
        params.endDate,
        params.type || QueryType.Filtered,
        params.security_tool
      )
      .pipe(
        catchError((error: any) => {
          console.error('Error fetching Automated Response Usage Data:', error);
          return of([]);
        })
      );
  }

  public fetchTicketClosureOutcomesData(orgId: number | number[], params: SocAnalyticsParams): Observable<any> {
    if (!params.startDate || !params.endDate) {
      console.error('Error: Invalid date range provided for fetchTicketClosureOutcomesData');
      return of(null);
    }

    return this.analyticsService
      .getTicketClosureOutcomesData(
        orgId,
        params.startDate,
        params.endDate,
        params.type || QueryType.Filtered,
        params.security_tool
      )
      .pipe(
        catchError((error: any) => {
          console.error('Error fetching Ticket Closure Outcomes Data:', error);
          return of(null);
        })
      );
  }

  // Helper method to convert date range to proper format for closure outcomes
  public async getTicketClosureOutcomesData(
    orgId: number,
    dateRange: { startDate: string; endDate: string },
    securityTool?: string | undefined
  ): Promise<any[]> {
    const params: SocAnalyticsParams = {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    };
    if (securityTool) {
      params['security_tool'] = securityTool;
    }

    return firstValueFrom(this.fetchTicketClosureOutcomesData(orgId, params));
  }
}
